/**
 * "Pin for all": a host or co-host pins one tile on everybody's screen.
 *
 * The tile menu offered the item and wired it to the same handler as "Pin for
 * me", so it only ever toggled the sender's own pin — and on a tile already
 * pinned for yourself it UNPINNED it. Nothing was sent; nobody else's screen
 * changed.
 *
 * This is the decision half, as `layout-sync` is for layouts: what to send and
 * what to believe. It rides the generic `broadcast` event for the same reason
 * (a bespoke event an older server does not implement would take the sender's
 * channel down).
 *
 * ## Who may move everyone's screen
 *
 * Only a moderator — and that is checked on RECEIPT, against the sender the
 * server stamped on the broadcast (`from`), not against anything in the body.
 * The menu only offers the item to moderators, but a hidden menu item is not a
 * permission: any participant could send the envelope by hand.
 */

export type PinSyncEnvelope = {
  __inmeet?: unknown
  action?: unknown
  participantId?: unknown
}

export type PinSyncCommand = { action: 'set'; participantId: string } | { action: 'clear' }

export function pinForAllBody(participantId: string) {
  return { __inmeet: 'pin', action: 'set', participantId }
}

export function unpinForAllBody() {
  return { __inmeet: 'pin', action: 'clear' }
}

/**
 * The command an incoming broadcast carries, or null to ignore it.
 *
 * Own messages are dropped: the sender already applied the pin before sending,
 * and every client receives its own broadcasts.
 */
export function readPinSync(
  payload: unknown,
  selfId: string | null | undefined,
  isModerator: (peerId: string) => boolean,
): PinSyncCommand | null {
  const from = (payload as { from?: unknown })?.from
  const env = ((payload as { body?: PinSyncEnvelope })?.body ?? {}) as PinSyncEnvelope
  if (env.__inmeet !== 'pin') return null
  if (typeof from !== 'string' || !from) return null
  if (selfId && from === selfId) return null
  if (!isModerator(from)) return null
  if (env.action === 'clear') return { action: 'clear' }
  if (env.action === 'set' && typeof env.participantId === 'string' && env.participantId) {
    return { action: 'set', participantId: env.participantId }
  }
  return null
}
